import React, { useEffect, useState } from 'react';
import { DNABox, DNAText } from '@alucio/lux-ui';
import { useUserTenant } from '../../../state/redux/selector/user';
import { getExternalOidcVaultCrmSsoConfig } from './config';
import {
  EXTERNAL_OIDC_VAULT_CRM_SSO_CODE_VERIFIER,
  EXTERNAL_OIDC_VAULT_CRM_SSO_STATE,
} from './constants';
import { createPkceCodeChallenge, createRandomString } from './pkce';
import { ExternalOidcVaultCrmSsoConfig, SsoTemporaryStorage } from './types';

type StartExternalOidcVaultCrmSsoInput = {
  storage: Pick<SsoTemporaryStorage, 'setItem'>,
  location: Pick<Location, 'assign'>,
  config: ExternalOidcVaultCrmSsoConfig,
  createState?: () => string,
  createCodeVerifier?: () => string,
  createCodeChallenge?: (codeVerifier: string) => Promise<string>,
}

export const startExternalOidcVaultCrmSso = async ({
  storage,
  location,
  config,
  createState = () => createRandomString(),
  createCodeVerifier = () => createRandomString(64),
  createCodeChallenge = createPkceCodeChallenge,
}: StartExternalOidcVaultCrmSsoInput): Promise<void> => {
  const state = createState();
  const codeVerifier = createCodeVerifier();
  const codeChallenge = await createCodeChallenge(codeVerifier);

  storage.setItem?.(EXTERNAL_OIDC_VAULT_CRM_SSO_STATE, state);
  storage.setItem?.(EXTERNAL_OIDC_VAULT_CRM_SSO_CODE_VERIFIER, codeVerifier);

  const authorizationUrl = new URL(config.authorizationEndpoint);
  authorizationUrl.search = new URLSearchParams({
    response_type: 'code',
    client_id: config.clientId,
    redirect_uri: config.redirectUri,
    scope: config.scope,
    state,
    code_challenge: codeChallenge,
    code_challenge_method: 'S256',
  }).toString();

  location.assign(authorizationUrl.toString());
};

const ExternalOidcVaultCrmSsoStart: React.FC = () => {
  const [error, setError] = useState('');
  const userTenant = useUserTenant();

  useEffect(() => {
    if (typeof window === 'undefined' || !userTenant) {
      return;
    }

    setError('');

    let config: ExternalOidcVaultCrmSsoConfig;

    try {
      config = getExternalOidcVaultCrmSsoConfig(
        userTenant.config.crmIntegration?.externalOidcSso,
      );
    } catch (startError) {
      setError(startError instanceof Error ? startError.message : 'Unable to start SSO.');
      return;
    }

    startExternalOidcVaultCrmSso({
      storage: window.sessionStorage,
      location: window.location,
      config,
    }).catch((startError) => {
      setError(startError instanceof Error ? startError.message : 'Unable to start SSO.');
    });
  }, [userTenant]);

  return (
    <DNABox fill alignX="center" alignY="center" style={{ padding: 24 }}>
      <DNAText>{error || 'Starting external OIDC SSO...'}</DNAText>
    </DNABox>
  );
};

export default ExternalOidcVaultCrmSsoStart;
